import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Routes } from '../utils/Routes';
import { Document, Page, pdfjs } from 'react-pdf';
import HTMLFlipBook from 'react-pageflip';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type ViewMode = 'single' | 'double';

const BookPage = React.forwardRef<
  HTMLDivElement,
  { pageNumber: number; width: number; height: number }
>(({ pageNumber, width, height }, ref) => (
  <div ref={ref} className="bg-white flex items-center justify-center">
    <Page
      pageNumber={pageNumber}
      width={width}
      height={height}
      renderAnnotationLayer={false}
      renderTextLayer={false}
    />
  </div>
));

BookPage.displayName = 'BookPage';

function useBookDimensions(viewMode: ViewMode) {
  const [dimensions, setDimensions] = useState({ width: 400, height: 566 });

  useEffect(() => {
    function calculate() {
      const vw = window.innerWidth;
      const vh = window.innerHeight - 120; // account for top bar + controls
      const ratio = 210 / 297; // A4 aspect ratio

      let pageWidth: number;
      let pageHeight: number;

      if (viewMode === 'single') {
        // Single-page: one page centered
        pageWidth = Math.min(vw - 32, 500);
        pageHeight = pageWidth / ratio;
        if (pageHeight > vh) {
          pageHeight = vh;
          pageWidth = pageHeight * ratio;
        }
      } else {
        // Double-page: two pages side by side — always fit both on screen
        pageHeight = Math.min(vh, 700);
        pageWidth = pageHeight * ratio;
        if (pageWidth * 2 > vw - 32) {
          pageWidth = (vw - 32) / 2;
          pageHeight = pageWidth / ratio;
        }
      }

      setDimensions({
        width: Math.floor(pageWidth),
        height: Math.floor(pageHeight),
      });
    }

    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, [viewMode]);

  return dimensions;
}

export default function ArtPublication() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const edition = searchParams.get('edition') === '2' ? 2 : 1;
  const isFullView = searchParams.get('view') === 'book';

  const pdfFileName = isFullView
    ? edition === 2
      ? 'Unspoken Art_Publication_Edition_02_Full_View.pdf'
      : 'Unspoken Art_Publication_Edition_01_Full_View.pdf'
    : edition === 2
      ? 'Unspoken Art - Edition II (preview).pdf'
      : 'Unspoken Art - Edition I (preview).pdf';
  const PDF_URL = `${process.env.PUBLIC_URL}/assets/${encodeURIComponent(pdfFileName)}`;
  const editionTitle = `Unspoken Art | Edition ${edition === 2 ? 'II' : 'I'}`;

  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('double');
  const flipBookRef = useRef<any>(null);
  const { width, height } = useBookDimensions(viewMode);

  // Force remount of HTMLFlipBook when viewMode changes (it doesn't support dynamic usePortrait)
  const [flipKey, setFlipKey] = useState(0);
  useEffect(() => {
    setFlipKey((k) => k + 1);
    setCurrentPage(0);
  }, [viewMode]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      setLoading(false);
    },
    [],
  );

  const handleFlip = useCallback((e: any) => {
    setCurrentPage(e.data);
  }, []);

  const goToPrev = () => {
    flipBookRef.current?.pageFlip()?.flipPrev();
  };

  const goToNext = () => {
    flipBookRef.current?.pageFlip()?.flipNext();
  };

  return (
    <div className="fixed inset-0 bg-gray-800 z-50 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900 text-white shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <h1 className="publication-title truncate">{editionTitle}</h1>
          {!isFullView && (
            <span className="shrink-0 text-[10px] font-medium tracking-wide uppercase bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5">
              Preview
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 ml-4 shrink-0">
          {/* View mode toggle */}
          <div className="flex rounded overflow-hidden border border-gray-600">
            <button
              onClick={() => setViewMode('single')}
              className={`px-2.5 py-1 text-xs transition-colors ${
                viewMode === 'single'
                  ? 'bg-gray-100 text-gray-900'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              aria-label="Single page view"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="6" y="3" width="12" height="18" rx="1" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('double')}
              className={`px-2.5 py-1 text-xs transition-colors ${
                viewMode === 'double'
                  ? 'bg-gray-100 text-gray-900'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              aria-label="Double page view"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="3" width="9" height="18" rx="1" />
                <rect x="13" y="3" width="9" height="18" rx="1" />
              </svg>
            </button>
          </div>
          {/* Download button */}
          {/* <a
            href={PDF_URL}
            download
            className="text-gray-300 hover:text-white transition-colors"
            aria-label="Download PDF"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </a> */}
          {/* Close button */}
          <button
            onClick={() => navigate(Routes.Things)}
            className="text-gray-300 hover:text-red-500 transition-colors text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>
      </div>

      {/* Book area */}
      <div className="flex-1 flex items-center justify-center overflow-hidden">
        <Document
          file={PDF_URL}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="text-white text-sm animate-pulse">
              Loading publication&hellip;
            </div>
          }
          error={
            <div className="text-red-400 text-sm">
              Failed to load publication. Please try again.
            </div>
          }
        >
          {loading ? null : (
            // @ts-ignore — HTMLFlipBook prop types are overly strict (all props marked required)
            <HTMLFlipBook
              key={flipKey}
              ref={flipBookRef}
              width={width}
              height={height}
              size="fixed"
              minWidth={100}
              maxWidth={800}
              minHeight={141}
              maxHeight={1132}
              showCover={true}
              usePortrait={viewMode === 'single'}
              drawShadow={true}
              maxShadowOpacity={0.5}
              flippingTime={600}
              mobileScrollSupport={false}
              onFlip={handleFlip}
              className=""
              style={{}}
              startPage={0}
              autoSize={false}
              startZIndex={0}
              clickEventForward={true}
              useMouseEvents={true}
              swipeDistance={30}
              showPageCorners={true}
              disableFlipByClick={false}
            >
              {Array.from({ length: numPages }, (_, i) => (
                <BookPage
                  key={i}
                  pageNumber={i + 1}
                  width={width}
                  height={height}
                />
              ))}
            </HTMLFlipBook>
          )}
        </Document>
      </div>

      {/* Controls */}
      {numPages > 0 && (
        <div className="flex items-center justify-center gap-6 py-3 bg-gray-900 text-white shrink-0">
          <button
            onClick={goToPrev}
            disabled={currentPage === 0}
            className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            &#8592;
          </button>
          <span className="text-sm text-gray-300">
            {currentPage + 1} / {numPages}
          </span>
          <button
            onClick={goToNext}
            disabled={currentPage >= numPages - 1}
            className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            &#8594;
          </button>
        </div>
      )}
    </div>
  );
}
